import { useCallback, useEffect, useState, type MutableRefObject, type RefObject } from 'react';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { useScriptBrowserStore } from '../../../script-browser/store/scriptBrowserStore';
import { isUntouchedPendingReload } from '../../../sessions/services/sessionPersistenceCap';
import { openSqlFile, saveSqlFile } from '../services/queryFilesApi';
import { getFileNameFromPath, hashText } from '../utils/queryFileUtils';
import { useExternalFileChange } from './useExternalFileChange';
import { toast } from '../../../../components/ui/toaster';
import type { QueryEditorHandle } from '../components/QueryEditor';
import { readLiveQuery } from '../components/liveQuery';

type UseQueryFileSyncParams = {
  tabId: string;
  /** The tab's bound .sql file path, if any. */
  filePath: string | undefined;
  /**
   * File whose script the persisted snapshot omitted (over the persisted-SQL
   * cap, see editorTextLimits.ts) — the tab mounted detached with a
   * placeholder and reloads the script from disk here.
   */
  pendingFileReload: string | undefined;
  /** Current tab title — seeds the suggested file name for Save dialogs. */
  tabTitle: string;
  connectionId: string | undefined;
  connectionName: string | undefined;
  editorRef: RefObject<QueryEditorHandle | null>;
  /** Synchronous truth for the editor buffer (see host component). */
  queryRef: MutableRefObject<string>;
  /** Tracks the last query written to the store (external-change detection). */
  lastStoredQueryRef: MutableRefObject<string>;
  setQuery: (query: string) => void;
};

/**
 * File-sync surface for a query editor tab: Open / Save / Save As against
 * .sql files on disk, tab-title-from-file-name, script-browser recording, and
 * the external-file-change prompt (reload vs keep). All buffer writes keep the
 * four representations in sync (queryRef, lastStoredQueryRef, React state,
 * Monaco model) exactly as the previous inline implementation did.
 */
export function useQueryFileSync({
  tabId,
  filePath,
  pendingFileReload,
  tabTitle,
  connectionId,
  connectionName,
  editorRef,
  queryRef,
  lastStoredQueryRef,
  setQuery,
}: UseQueryFileSyncParams) {
  const updateTabContent = useWorkspaceStore((state) => state.updateTabContent);

  const [isOpeningFile, setIsOpeningFile] = useState(false);
  const [isSavingFile, setIsSavingFile] = useState(false);

  const updateTabTitleFromFilePath = useCallback((nextFilePath: string) => {
    const fileName = getFileNameFromPath(nextFilePath);
    if (!fileName) return;
    useWorkspaceStore.getState().updateTabTitle(tabId, fileName, false);
  }, [tabId]);

  const handleOpenFile = useCallback(async () => {
    setIsOpeningFile(true);
    try {
      const result = await openSqlFile();
      if (!result.success) {
        if (!result.cancelled) {
          toast.error({ title: 'Open failed', description: result.error });
        }
        return;
      }

      const openedQuery = result.query ?? '';
      queryRef.current = openedQuery;
      lastStoredQueryRef.current = openedQuery;
      setQuery(openedQuery);
      editorRef.current?.setValue(openedQuery);
      updateTabContent(tabId, {
        query: openedQuery,
        filePath: result.filePath,
        fileLastSavedHash: hashText(openedQuery),
        resultSets: [],
        messages: [],
        // Cleared with the log they index into — a stale offset would outlive
        // the messages it was measured against.
        messagesRunStart: undefined,
        messagesRunEnd: undefined,
        results: [],
        columns: [],
        executionPlan: undefined,
      });
      updateTabTitleFromFilePath(result.filePath);
      useScriptBrowserStore.getState().recordScriptOpened(result.filePath, openedQuery.slice(0, 100));
      toast.success({ title: 'Opened', description: result.filePath });
    } finally {
      setIsOpeningFile(false);
    }
  }, [tabId, updateTabContent, updateTabTitleFromFilePath, editorRef, queryRef, lastStoredQueryRef, setQuery]);

  const buildSuggestedFileName = useCallback((): string => {
    const fromPath = filePath ? getFileNameFromPath(filePath) : '';
    if (fromPath) return fromPath;
    return tabTitle.toLowerCase().endsWith('.sql') ? tabTitle : `${tabTitle}.sql`;
  }, [tabTitle, filePath]);

  // Reload a script the persisted snapshot omitted. A successful read
  // restores the script and re-attaches the file. On failure the placeholder
  // stays and the tab remains detached, so Save can never write the
  // placeholder over the real file.
  useEffect(() => {
    if (!pendingFileReload) return;
    let cancelled = false;
    const placeholderAtStart = queryRef.current;
    void openSqlFile({ filePath: pendingFileReload }).then((result) => {
      if (cancelled) return;
      if (result.success) {
        // Apply only while BOTH copies of the text are still the untouched
        // placeholder: the editor's (queryRef) and the store's — a write that
        // arrived through the store (MCP set-editor-text, AI Workspace) may
        // not have reached queryRef yet when the read resolves.
        const current = useWorkspaceStore.getState().tabs.find((t) => t.id === tabId);
        const stillPending =
          current?.type === 'query' &&
          current.content.pendingFileReload === pendingFileReload &&
          isUntouchedPendingReload(current.content);
        if (!stillPending || queryRef.current !== placeholderAtStart) {
          // The tab moved on (user typed over the placeholder, opened another
          // file here, the store was written, or the reload was superseded):
          // keep the newer state.
          updateTabContent(tabId, { pendingFileReload: undefined });
          return;
        }
        const reloaded = result.query ?? '';
        queryRef.current = reloaded;
        lastStoredQueryRef.current = reloaded;
        setQuery(reloaded);
        editorRef.current?.setValue(reloaded);
        updateTabContent(tabId, {
          query: reloaded,
          filePath: result.filePath,
          fileLastSavedHash: hashText(reloaded),
          pendingFileReload: undefined,
        });
        return;
      }
      updateTabContent(tabId, { pendingFileReload: undefined });
      toast.error({ title: 'Could not reload script', description: result.error });
    });
    return () => {
      cancelled = true;
    };
  }, [pendingFileReload, tabId, updateTabContent, editorRef, queryRef, lastStoredQueryRef, setQuery]);

  const handleSaveFile = useCallback(async () => {
    // Read directly from Monaco — `query` state lags by `flushQueryDebounced`'s
    // window so it can be stale when the user saves immediately after typing.
    const liveQuery = readLiveQuery(editorRef.current, queryRef.current, 'Save failed');
    if (liveQuery === null) return;
    setIsSavingFile(true);
    try {
      const result = await saveSqlFile({
        query: liveQuery,
        filePath,
        forceDialog: !filePath,
        suggestedFileName: buildSuggestedFileName(),
      });

      if (!result.success) {
        if (!result.cancelled) {
          toast.error({ title: 'Save failed', description: result.error });
        }
        return;
      }

      updateTabContent(tabId, {
        filePath: result.filePath,
        fileLastSavedHash: hashText(liveQuery),
      });
      updateTabTitleFromFilePath(result.filePath);
      useScriptBrowserStore.getState().recordScriptSaved(result.filePath, hashText(liveQuery), {
        connectionId,
        connectionName,
      });
      toast.success({ title: 'Saved', description: result.filePath });
    } finally {
      setIsSavingFile(false);
    }
  }, [buildSuggestedFileName, connectionId, connectionName, filePath, tabId, updateTabContent, updateTabTitleFromFilePath, editorRef, queryRef]);

  const handleSaveFileAs = useCallback(async () => {
    const liveQuery = readLiveQuery(editorRef.current, queryRef.current, 'Save As failed');
    if (liveQuery === null) return;
    setIsSavingFile(true);
    try {
      const result = await saveSqlFile({
        query: liveQuery,
        filePath,
        forceDialog: true,
        suggestedFileName: buildSuggestedFileName(),
      });

      if (!result.success) {
        if (!result.cancelled) {
          toast.error({ title: 'Save As failed', description: result.error });
        }
        return;
      }

      updateTabContent(tabId, {
        filePath: result.filePath,
        fileLastSavedHash: hashText(liveQuery),
      });
      updateTabTitleFromFilePath(result.filePath);
      useScriptBrowserStore.getState().recordScriptSaved(result.filePath, hashText(liveQuery), {
        connectionId,
        connectionName,
      });
      toast.success({ title: 'Saved As', description: result.filePath });
    } finally {
      setIsSavingFile(false);
    }
  }, [buildSuggestedFileName, connectionId, connectionName, filePath, tabId, updateTabContent, updateTabTitleFromFilePath, editorRef, queryRef]);

  // External file change handling — watch while tab is open, prompt when the tab is next active.
  const { pendingChange, clearPending } = useExternalFileChange(tabId, filePath);
  const handleExternalReload = useCallback(() => {
    if (!pendingChange || pendingChange.kind !== 'changed') {
      clearPending();
      return;
    }
    const newContent = pendingChange.newContent;
    queryRef.current = newContent;
    lastStoredQueryRef.current = newContent;
    setQuery(newContent);
    editorRef.current?.setValue(newContent);
    updateTabContent(tabId, {
      query: newContent,
      fileLastSavedHash: hashText(newContent),
    });
    clearPending();
  }, [clearPending, pendingChange, tabId, updateTabContent, editorRef, queryRef, lastStoredQueryRef, setQuery]);
  const handleExternalKeep = useCallback(() => {
    if (pendingChange?.kind === 'changed') {
      // Update the on-disk baseline so the tab shows as dirty against the new file contents.
      updateTabContent(tabId, { fileLastSavedHash: hashText(pendingChange.newContent) });
    } else if (pendingChange?.kind === 'deleted') {
      // File is gone: clear baseline so saving will write fresh.
      updateTabContent(tabId, { fileLastSavedHash: undefined });
    }
    clearPending();
  }, [clearPending, pendingChange, tabId, updateTabContent]);

  return {
    isOpeningFile,
    isSavingFile,
    handleOpenFile,
    handleSaveFile,
    handleSaveFileAs,
    pendingChange,
    handleExternalReload,
    handleExternalKeep,
  };
}
